import { Camera, ImageOff, ImagePlus, X } from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogTitle,
} from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import type { InspectionColorSet } from '../../constants/inspection-colors'

interface InspectionPhotoFieldProps {
	attachLabel: string
	altText: string
	preview: string | null
	onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
	onRemove: () => void
	colors: InspectionColorSet
	compact?: boolean
	showRemoveButton?: boolean
	showPickerWhenEmpty?: boolean
	hideOnPreviewError?: boolean
}

export const InspectionPhotoField = ({
	attachLabel,
	altText,
	preview,
	onFileChange,
	onRemove,
	colors,
	compact = false,
	showRemoveButton = true,
	showPickerWhenEmpty = true,
	hideOnPreviewError = false,
}: InspectionPhotoFieldProps) => {
	const [lightboxOpen, setLightboxOpen] = useState(false)
	const [failedPreviewSrc, setFailedPreviewSrc] = useState<string | null>(null)
	const isDesktop = useMediaQuery('(min-width: 768px)')

	const galleryInputRef = useRef<HTMLInputElement | null>(null)
	const cameraInputRef = useRef<HTMLInputElement | null>(null)
	const inputId = useId()
	const resolvedPreview =
		preview && preview !== failedPreviewSrc ? preview : null
	const shouldShowMissingPreviewFallback =
		Boolean(preview) && !resolvedPreview && hideOnPreviewError

	return (
		<>
			<Field className="w-full min-w-0 space-y-2">
				{resolvedPreview ? (
					<div className="relative w-fit flex justify-center">
						<img
							src={resolvedPreview}
							alt={altText}
							className="h-32 w-52 cursor-pointer rounded-lg object-contain sm:h-40 sm:w-64"
							onClick={() => setLightboxOpen(true)}
							onError={() => {
								if (hideOnPreviewError && preview) {
									setFailedPreviewSrc(preview)
								}
							}}
						/>
						{showRemoveButton && (
							<Button
								type="button"
								variant="close-dark"
								size="icon-circle-sm"
								onClick={onRemove}
								className="absolute -right-2 -top-2"
							>
								<X className="size-3.5" />
							</Button>
						)}
					</div>
				) : shouldShowMissingPreviewFallback ? (
					<div className="flex h-32 w-52 flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 text-muted-foreground sm:h-40 sm:w-64">
						<ImageOff className="size-6" />
						<span className="text-xs font-medium">Imagem nao encontrada</span>
					</div>
				) : showPickerWhenEmpty ? (
					<div
						className={cn(
							'grid w-full min-w-0 max-w-full gap-3',
							compact && !isDesktop && 'grid-cols-2 gap-2',
						)}
					>
						{!isDesktop && (
							<Button
								type="button"
								variant="outline"
								className={cn(
									'h-auto flex-col rounded-lg border-2 border-dashed',
									compact ? 'min-h-20 gap-1.5 p-3' : 'min-h-28 gap-2 p-6',
									compact && 'whitespace-normal',
									colors.photoZone,
								)}
								onClick={() => cameraInputRef.current?.click()}
							>
								<Camera className={cn(compact ? 'size-6' : 'size-8')} />
								<span
									className={cn(
										'text-sm font-medium',
										compact &&
											'whitespace-normal wrap-break-word text-center leading-tight',
									)}
								>
									Tirar foto
								</span>
							</Button>
						)}

						<Button
							type="button"
							variant="outline"
							className={cn(
								'h-auto flex-col rounded-lg border-2 border-dashed',
								compact ? 'min-h-20 gap-1.5 p-3' : 'min-h-28 gap-2 p-6',
								compact && 'whitespace-normal',
								colors.photoZone,
							)}
							onClick={() => galleryInputRef.current?.click()}
						>
							<ImagePlus className={cn(compact ? 'size-6' : 'size-8')} />
							<span
								className={cn(
									'text-sm font-medium',
									compact &&
										'whitespace-normal wrap-break-word text-center leading-tight',
								)}
							>
								{attachLabel}
							</span>
						</Button>

						<input
							ref={cameraInputRef}
							id={`${inputId}-camera`}
							type="file"
							accept="image/*"
							capture="environment"
							onChange={onFileChange}
							className="sr-only w-px! max-w-px!"
						/>

						<input
							ref={galleryInputRef}
							id={`${inputId}-gallery`}
							type="file"
							accept="image/*"
							onChange={onFileChange}
							className="sr-only w-px! max-w-px!"
						/>
					</div>
				) : null}
			</Field>

			<Dialog
				open={lightboxOpen && Boolean(resolvedPreview)}
				onOpenChange={setLightboxOpen}
			>
				<DialogContent
					showCloseButton={false}
					className="left-0 top-0 h-dvh w-screen max-w-none translate-x-0 translate-y-0 rounded-none border-0 bg-black/90 p-2 shadow-none sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-auto sm:max-w-[95vw] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:bg-transparent sm:p-0"
				>
					<DialogTitle className="sr-only">{altText}</DialogTitle>
					<div className="relative flex h-full w-full items-center justify-center sm:inline-block sm:h-auto sm:w-auto sm:rounded-xl sm:ring-1 sm:ring-white/20">
						<img
							src={resolvedPreview ?? ''}
							alt={altText}
							className="block max-h-[92dvh] max-w-[96vw] object-contain sm:max-h-[88vh] sm:max-w-[85vw]"
						/>
						<DialogClose asChild>
							<Button
								variant="close-light"
								size="icon-circle"
								className="absolute right-3 top-3 sm:right-2 sm:top-2"
							>
								<X className="size-4" />
							</Button>
						</DialogClose>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
